import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from "@angular/core";
import { UserResponse } from "algorilla-shared-libs";
import { NotificationsService } from "bowman-angular2-notifications";
import { BowmanCacheService } from "bowman-cache";
import { CrudActionType, ErrorMessage, TableFilterPipe } from "bowman-commons";
import { Subscription } from "rxjs";
import { FlowTestResult } from "src/app/dashboard/flow-inspect/model/flow-test-result";
import { ManagementService } from "src/app/services/management.service";
import { QueryManagerService } from "src/app/services/query-manager.service";
import { TypeMappingService } from "src/services/type-mapping.service";
import { DrawFlowDto, DrawFlowNode } from "../model/drawflow-dto";
import { FlowTestEvent, FlowTestInstance, FlowTestObject } from "../model/flow-test-object";
import { StepQueryResultContainer } from "../model/step-query-result-container";
import { StepStatus } from "../model/step-status.enum";
import { FlowTypeEditorService } from "../service/flow-type-editor.service";
import { FlowTypeInspectService } from "../service/flow-type-inspect.service";

@Component({
    selector: "app-flow-management-test",
    templateUrl: "./flow-management-test.component.html",
    providers: [FlowTypeEditorService, QueryManagerService, TableFilterPipe],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowManagementTestComponent implements OnInit, OnChanges {
    @Input() content: FlowTestResult;
    public stepList: { name: string; code: string }[];
    public user: UserResponse;
    public busy: Subscription;
    public errorMessage: ErrorMessage = new ErrorMessage();

    public testEventResponse: FlowTestInstance = null;
    public testEventCalendar: boolean = true;
    public testEvent: FlowTestObject = new FlowTestObject;
    public testEventInstanceString: string = null;

    // Test Event
    public statusOptions = Object.keys(StepStatus).filter(key => isNaN(Number(key))).map(key => ({ name: key, code: StepStatus[key as keyof typeof StepStatus] }));
    public inspectData: StepQueryResultContainer = null;

    public action: CrudActionType;
    public startTime = new Date();
    constructor(
        public _managementService: ManagementService,
        public _inspectService: FlowTypeInspectService,
        private _flowTypeEditorService: FlowTypeEditorService,
        private _cdr: ChangeDetectorRef,
        public tableFilterPipe: TableFilterPipe,
        private _typeMappingService: TypeMappingService,
        private _bowmanCacheService: BowmanCacheService,
        private _notificationService: NotificationsService

    ) {

    }


    instanceDetailsData(response: FlowTestInstance) {

        let result;
        let lastStep = response.lastStep + ' (' + this.getNameByCode(response.lastStep) + ')';
        result = [
            { key: 'Start Timestamp', value: response.startTs, type: 'timestamp' },
            { key: 'End Timestamp', value: response.endTs, type: 'timestamp' },
            { key: 'Status', value: response.status, type: 'text' },
            { key: 'Last Step', value: response.lastStep, type: 'text' },
            { key: 'Last Update', value: response.lastUpdate, type: 'timestamp' },
            { key: 'Matched Closure ID', value: response.matchedClosureId, type: 'text' },
            { key: 'Compliant', value: response.compliant, type: 'boolean' }
        ];
        Object.keys(response.processedSteps).map(key => {
            result.push({ key: key, value: response.processedSteps[key].timestamp, type: 'timestamp' })
        });
        return result;
    }
    getNameByCode(code: string): string {
        let node = this.getNodeByCode(code);
        if (node && node.data && node.data.code) {
            return node.data.name
        } else {
            if (node) {
                return node.name
            } else {
                return 'ERRORE:MISSING STEP'
            }
        }
    }
    public getNodeByCode(code: string): DrawFlowNode {
        return Object.values(this.content.drawFlowFullData.drawFlowData).filter(item => item.data.code === code)[0];
    }
    public sendTestEvent() {
        this.errorMessage.reset();
        // convertire this.testEventInstanceString in instance
        if (this.testEventInstanceString != null) {
            var instance = JSON.parse(this.testEventInstanceString);
            // Controllare se ci sono tutti i campi dell'istanza
            this.testEvent.instance = instance;
        }

        this._flowTypeEditorService.testEvent(this.content.drawFlowFullData.ft.code, this.testEvent).subscribe(
            (res) => {
                this.testEventResponse = res.result;
                this.testEventInstanceString = JSON.stringify(this.testEventResponse);

                // Convertire la risposta in inspect, mandarla al grafico e mostrarlo 
                this.inspectData = this.eventResponseToInspect(this.testEventResponse);
                this._cdr.detectChanges();
            },
            (err) => {
                this.errorMessage = new ErrorMessage("Error while sending test event.", err);
                this._cdr.detectChanges();
            });
    }

    public addTestEvent() {
        let tmpEvent = new FlowTestEvent;
        tmpEvent.flowTypeCode = this.content.drawFlowFullData.ft.code;
        this.testEvent.events.push(tmpEvent);
    }

    handleTimeChange(received: any, event: FlowTestEvent) {
        event.eventTs = new Date(received.start).getTime();
    }


    public removeTestEvent(index: number) {
        if (index > -1 && index < this.testEvent.events.length) {
            this.testEvent.events.splice(index, 1);
        } else {
            console.error("Indice non valido");
        }
        this._cdr.detectChanges();
    }
    public eventResponseToInspect(testEventResponse): StepQueryResultContainer {
        let inspectResponse = new StepQueryResultContainer;

        let stepCounters: { [key: string]: number } = {};
        for (let step in testEventResponse.processedSteps) {
            stepCounters[step] = 1; // Inizializza ogni step counter a 1
        }

        // Creazione arcDurations
        let arcDurations = [];
        let minDurationMs = 0;
        let maxDurationMs = 0;
        for (let key in testEventResponse.leadTimes) {
            let [parent, child] = key.split('_');
            arcDurations.push({
                avgDurationMs: testEventResponse.leadTimes[key],
                minDurationMs: testEventResponse.leadTimes[key],
                maxDurationMs: testEventResponse.leadTimes[key],
                parentStepCode: parent,
                childStepCode: child
            });

            if (minDurationMs == 0 || minDurationMs > (testEventResponse.leadTimes[key])) {
                minDurationMs = testEventResponse.leadTimes[key]
            }

            if (maxDurationMs == 0 || maxDurationMs < (testEventResponse.leadTimes[key])) {
                maxDurationMs = testEventResponse.leadTimes[key]
            }
        }

        let avgDurationMs = (minDurationMs + maxDurationMs) / 2;

        const instanceDuration = {
            minDurationMs,
            maxDurationMs,
            avgDurationMs
        };

        inspectResponse = {
            'queryTime': 0,
            stepCounters,
            arcDurations,
            instanceDuration
        };

        return inspectResponse;
    }

    ngOnInit() {
        this.testEventResponse = new FlowTestInstance();
        this.testEvent = new FlowTestObject();
        let tmpEvent = new FlowTestEvent();
        tmpEvent.flowTypeCode = this.content.drawFlowFullData.ft.code;
        this.testEvent.events.push(tmpEvent);
        this.testEventInstanceString = null;

        this.stepList = Object.values(this.content.drawFlowFullData.drawFlowData)
            .map(item => ({
                name: item.data.name,
                code: item.data.code
            }))
            .sort((a, b) => a.code.localeCompare(b.code));

        if (this._bowmanCacheService.has('algorilla_user')) {
            this._bowmanCacheService.get('algorilla_user').subscribe(user => {
                this.user = user;

            });
        }
    }
    ngOnChanges(changes: SimpleChanges): void {
    }
}
